import { createElement, useCallback, useEffect, useRef } from 'react';

import { RECAPTCHA_ENABLED_ENVIRONMENTS } from '@/constants/config';
import { Route as rootRoute } from '@/routes/__root';

/** v3 script or execute did not finish within the client load timeout (show v2 fallback). */
export class RecaptchaV3TimeoutError extends Error {
  override readonly name = 'RecaptchaV3TimeoutError';

  constructor() {
    super('reCAPTCHA timed out');
  }
}

/** True for our client load/execute timeout (subclass or legacy plain Error message). */
export function isRecaptchaV3LoadTimeoutError(error: unknown): boolean {
  if (error instanceof RecaptchaV3TimeoutError) {
    return true;
  }
  return error instanceof Error && error.message === 'reCAPTCHA timed out';
}

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark';
          size?: 'normal' | 'compact';
        },
      ) => number;
      reset: (widgetId: number) => void;
    };
  }
}

export function useRecaptcha() {
  const { env } = rootRoute.useRouteContext();
  const siteKey = env.RECAPTCHA_V3_SITE_KEY;
  const isDisabled = !RECAPTCHA_ENABLED_ENVIRONMENTS.has(env.ENVIRONMENT);

  const getToken = useCallback(
    (action: string): Promise<string> => {
      if (isDisabled) {
        return Promise.resolve('recaptcha-disabled');
      }

      /** Slow networks / CPU; v2 fallback if this fires. */
      const LOAD_TIMEOUT_MS = 15_000;

      const tokenPromise = new Promise<string>((resolve, reject) => {
        if (!siteKey) {
          reject(new Error('reCAPTCHA site key not configured'));
          return;
        }

        // Google's recommended grecaptcha.ready stub: queues the callback
        // if the script hasn't loaded yet, then flushes once it does.
        // See: https://developers.google.com/recaptcha/docs/v3
        const grecaptcha = globalThis.window.grecaptcha;
        if (!grecaptcha) {
          globalThis.window.grecaptcha = {
            ready: (cb: () => void) => {
              const check = () => {
                if ('execute' in globalThis.window.grecaptcha) {
                  cb();
                } else {
                  setTimeout(check, 100);
                }
              };
              check();
            },
          } as Window['grecaptcha'];
        }

        globalThis.window.grecaptcha.ready(() => {
          globalThis.window.grecaptcha
            .execute(siteKey, { action })
            .then(resolve)
            .catch(reject);
        });
      });

      // If adblockers prevent the recaptcha script from loading
      // We need a way to not keep the loading state spinning forever
      const timeoutPromise = new Promise<never>((_resolve, reject) =>
        setTimeout(
          () => reject(new RecaptchaV3TimeoutError()),
          LOAD_TIMEOUT_MS,
        ),
      );

      return Promise.race([tokenPromise, timeoutPromise]);
    },
    [siteKey, isDisabled],
  );

  return { getToken };
}

export function RecaptchaScript() {
  const { env } = rootRoute.useRouteContext();
  if (
    !env.RECAPTCHA_V3_SITE_KEY ||
    !RECAPTCHA_ENABLED_ENVIRONMENTS.has(env.ENVIRONMENT)
  )
    return null;
  return createElement('script', {
    async: true,
    src: `https://www.google.com/recaptcha/api.js?render=${env.RECAPTCHA_V3_SITE_KEY}`,
  });
}

/**
 * Renders the reCAPTCHA v2 checkbox widget. Loaded on-demand when v3 fails.
 * Uses the explicit render mode via a separate script tag so it doesn't
 * conflict with the v3 script already on the page.
 */
export function RecaptchaV2Challenge({
  onVerify,
  onExpire,
  resetRef,
}: {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  resetRef?: { current: (() => void) | null };
}) {
  const { env } = rootRoute.useRouteContext();
  const isDisabled = !RECAPTCHA_ENABLED_ENVIRONMENTS.has(env.ENVIRONMENT);
  const v2SiteKey = isDisabled ? '' : env.RECAPTCHA_V2_SITE_KEY;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const renderedRef = useRef(false);
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!resetRef) return;
    resetRef.current = () => {
      if (widgetIdRef.current !== null) {
        globalThis.window.grecaptcha.reset(widgetIdRef.current);
      }
    };
    return () => {
      resetRef.current = null;
    };
  }, [resetRef]);

  useEffect(() => {
    if (!v2SiteKey || !containerRef.current || renderedRef.current) return;

    globalThis.window.grecaptcha.ready(() => {
      if (!containerRef.current || renderedRef.current) return;
      renderedRef.current = true;
      widgetIdRef.current = globalThis.window.grecaptcha.render(
        containerRef.current,
        {
          sitekey: v2SiteKey,
          callback: (token: string) => {
            onVerifyRef.current(token);
          },
          'expired-callback': () => {
            onExpireRef.current?.();
          },
        },
      );
    });
  }, [v2SiteKey]);

  if (!v2SiteKey) return null;

  return createElement('div', { ref: containerRef });
}
